import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { RiBookReadLine, RiDownloadLine, RiArrowRightUpLine, RiLoader4Line } from 'react-icons/ri';
import { useNavigate, Link } from 'react-router-dom';
import { API_BASE_URL, ASSET_URL } from '../config';

const PublicationSection = () => {
    const [publications, setPublications] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchLatest = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/archive/read.php`);
                const data = await response.json();
                if (response.ok) {
                    // Show only the latest 3
                    setPublications((data.records || []).slice(0, 3));
                }
            } catch (error) {
                console.error('Error fetching latest publications:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchLatest();
    }, []);

    const handleOpenAsset = (pub) => {
        const targetUrl = pub.doi ? pub.doi : (pub.file_url?.startsWith('http') ? pub.file_url : (pub.file_url ? `${ASSET_URL}${pub.file_url}` : null));
        if (targetUrl) {
            window.open(targetUrl, '_blank');
        } else {
            navigate('/publications');
        }
    };

    return (
        <section className="py-16 bg-zinc-50 overflow-hidden">
            <div className="mx-auto max-w-7xl px-6 lg:px-8">
                <div className="flex flex-col md:flex-row justify-between items-end gap-6 mb-12">
                    <div className="max-w-2xl">
                        <motion.span
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            className="text-[10px] font-black text-emerald-600 uppercase tracking-[0.4em]"
                        >
                            Scientific Archives
                        </motion.span>
                        <motion.h2
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.1 }}
                            className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 leading-[1.2] sm:leading-tight font-display"
                        >
                            Published <span className="text-emerald-600 italic">Research.</span>
                        </motion.h2>
                    </div>
                    <Link to="/publications">
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.2em] text-emerald-700 hover:text-emerald-900 transition-colors group"
                        >
                            Explore Archives
                            <RiArrowRightUpLine className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                        </motion.button>
                    </Link>
                </div>

                {loading ? (
                    <div className="flex justify-center py-20">
                        <RiLoader4Line className="animate-spin text-emerald-500 text-3xl" />
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {publications.map((item, index) => (
                            <motion.div
                                key={item.id || index}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                onClick={() => handleOpenAsset(item)}
                                className="group relative bg-white p-6 rounded-2xl border border-zinc-200 hover:border-emerald-500/30 transition-all duration-500 hover:shadow-xl hover:shadow-emerald-500/5 cursor-pointer"
                            >
                                <div className="flex items-start justify-between mb-6">
                                    <div className="p-3 bg-emerald-50 rounded-2xl text-emerald-600">
                                        <RiBookReadLine size={24} />
                                    </div>
                                    <span className="text-[10px] font-black text-zinc-400 uppercase tracking-widest">{item.publication_date}</span>
                                </div>

                                <span className="text-[10px] font-black text-emerald-600 uppercase tracking-widest mb-3 block">
                                    {item.category}
                                </span>
                                <h3 className="text-lg font-bold text-slate-900 leading-snug mb-4 group-hover:text-emerald-700 transition-colors line-clamp-2 min-h-[3.5rem] font-display">
                                    {item.title}
                                </h3>
                                <p className="text-sm text-slate-500 font-medium leading-relaxed mb-8 line-clamp-3 font-sans">
                                    {item.description}
                                </p>

                                <div className="flex items-center justify-between pt-6 border-t border-zinc-100">
                                    <span className="text-xs font-bold text-zinc-400 italic">By {item.author}</span>
                                    <button className="p-2 hover:bg-emerald-50 rounded-full text-emerald-600 transition-colors">
                                        <RiDownloadLine size={18} />
                                    </button>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
};

export default PublicationSection;
